/**
 * Reusable drag-and-drop animation.
 *
 * Two usage modes:
 *  1. React component  — <DragDropAnimation />
 *  2. Raw HTML string  — getDragDropAnimationHTML()  (for Shepherd tour steps)
 *
 * The animation shows three bars: bar 1 swaps down with bar 2, then bar 2
 * lifts and nests as a child of bar 0 (indented). A cursor follows the action.
 */

import React from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";

// ---------------------------------------------------------------------------
// Keyframes (Emotion — used by the React component)
// ---------------------------------------------------------------------------

// Bar 1 (middle): lifts, drops to bar 2's spot, turns grey (released)
const bar1Anim = keyframes`
  0%   { transform: translate(0, 0);    background-color: var(--drag-color); box-shadow: none; }
  6%   { transform: translate(0, -2px); background-color: var(--drag-color); box-shadow: 0 4px 8px rgba(0,0,0,0.15); }
  16%  { transform: translate(0, 24px); background-color: var(--drag-color); box-shadow: none; }
  26%  { transform: translate(0, 24px); background-color: var(--drag-color); }
  30%  { transform: translate(0, 24px); background-color: var(--static-color); }
  86%  { transform: translate(0, 24px); background-color: var(--static-color); }
  95%  { transform: translate(0, 0);    background-color: var(--static-color); }
  100% { transform: translate(0, 0);    background-color: var(--drag-color); }
`;

// Bar 2 (bottom): shifts up, gets grabbed, moves to nest as child of bar 0
const bar2Anim = keyframes`
  0%   { transform: translate(0, 0);     background-color: var(--static-color); box-shadow: none; }
  16%  { transform: translate(0, 0);     background-color: var(--static-color); }
  28%  { transform: translate(0, -24px); background-color: var(--static-color); }
  32%  { transform: translate(0, -24px); background-color: var(--drag-color); }
  38%  { transform: translate(0, -26px); background-color: var(--drag-color); box-shadow: 0 4px 8px rgba(0,0,0,0.15); }
  48%  { transform: translate(0, -48px); background-color: var(--drag-color); box-shadow: none; }
  58%  { transform: translate(20px, -24px); background-color: var(--drag-color); }
  76%  { transform: translate(20px, -24px); background-color: var(--drag-color); }
  86%  { transform: translate(0, -24px); background-color: var(--static-color); }
  95%  { transform: translate(0, 0);     background-color: var(--static-color); }
  100% { transform: translate(0, 0);     background-color: var(--static-color); }
`;

// Cursor: follows bar 1 down, releases, grabs bar 2, follows it up and to child position
const cursorAnim = keyframes`
  0%   { transform: translate(0, 0);     opacity: 1; }
  6%   { transform: translate(0, -2px);  }
  16%  { transform: translate(0, 24px);  }
  26%  { transform: translate(0, 24px);  }
  28%  { opacity: 0; }
  30%  { transform: translate(0, 0);     opacity: 0; }
  32%  { transform: translate(0, 0);     opacity: 1; }
  38%  { transform: translate(0, -2px);  }
  48%  { transform: translate(0, -24px); }
  58%  { transform: translate(20px, 0);  }
  76%  { transform: translate(20px, 0);  }
  80%  { opacity: 0; }
  92%  { transform: translate(0, 0);     opacity: 0; }
  96%  { transform: translate(0, 0);     opacity: 1; }
  100% { transform: translate(0, 0);     opacity: 1; }
`;

// ---------------------------------------------------------------------------
// React component
// ---------------------------------------------------------------------------

const DragDropAnimation = ({ sx }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        p: 1.5,
        bgcolor: isLight ? theme.palette.grey[100] : theme.palette.grey[50],
        borderRadius: 1,
        overflow: "hidden",
        "--drag-color": isLight
          ? theme.palette.primary.main
          : theme.palette.primary.light,
        "--static-color": theme.palette.grey[300],
        ...sx,
      }}
    >
      {/* Bar 0 — static top bar */}
      <Box sx={{ height: 20, borderRadius: 0.5, bgcolor: "var(--static-color)" }} />
      {/* Bar 1 — swaps down, turns grey */}
      <Box sx={{ height: 20, borderRadius: 0.5, position: "relative", zIndex: 1, animation: `${bar1Anim} 5s ease-in-out infinite` }} />
      {/* Bar 2 — shifts up, gets dragged to nest as child of bar 0 */}
      <Box sx={{ height: 20, borderRadius: 0.5, position: "relative", zIndex: 2, animation: `${bar2Anim} 5s ease-in-out infinite` }} />
      {/* Cursor arrow */}
      <Box
        sx={{
          position: "absolute",
          top: 40,
          left: 22,
          zIndex: 3,
          pointerEvents: "none",
          animation: `${cursorAnim} 5s ease-in-out infinite`,
        }}
      >
        <svg width="12" height="16" viewBox="0 0 12 16" fill="none">
          <path
            d="M1 1L1 12L3.8 8.8L6.2 14L7.8 13.2L5.4 7.6L9 7.6L1 1Z"
            fill={theme.palette.text.primary}
            stroke={theme.palette.background.paper}
            strokeWidth="0.8"
          />
        </svg>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Raw-HTML generator (for Shepherd tour steps)
// ---------------------------------------------------------------------------
// Shepherd renders `text` as innerHTML, so we need plain CSS keyframes
// instead of Emotion. The HTML reads --tour-* custom properties that
// TourProvider already syncs from the MUI theme.

const ANIMATION_ID = "t3-tour-drag-drop";

/**
 * Returns an HTML string containing the drag-drop animation.
 * Safe to embed directly in a Shepherd step's `text` property.
 * Uses CSS custom properties from tourTheme so it adapts to light/dark mode.
 */
export function getDragDropAnimationHTML() {
  return `
<style>
  @keyframes ${ANIMATION_ID}-bar1 {
    0%   { transform: translate(0, 0);    background-color: var(--tour-primary, #7cd4fd); box-shadow: none; }
    6%   { transform: translate(0, -2px); background-color: var(--tour-primary, #7cd4fd); box-shadow: 0 4px 8px rgba(0,0,0,0.15); }
    16%  { transform: translate(0, 24px); background-color: var(--tour-primary, #7cd4fd); box-shadow: none; }
    26%  { transform: translate(0, 24px); background-color: var(--tour-primary, #7cd4fd); }
    30%  { transform: translate(0, 24px); background-color: #bdbdbd; }
    86%  { transform: translate(0, 24px); background-color: #bdbdbd; }
    95%  { transform: translate(0, 0);    background-color: #bdbdbd; }
    100% { transform: translate(0, 0);    background-color: var(--tour-primary, #7cd4fd); }
  }
  @keyframes ${ANIMATION_ID}-bar2 {
    0%   { transform: translate(0, 0);     background-color: #bdbdbd; box-shadow: none; }
    16%  { transform: translate(0, 0);     background-color: #bdbdbd; }
    28%  { transform: translate(0, -24px); background-color: #bdbdbd; }
    32%  { transform: translate(0, -24px); background-color: var(--tour-primary, #7cd4fd); }
    38%  { transform: translate(0, -26px); background-color: var(--tour-primary, #7cd4fd); box-shadow: 0 4px 8px rgba(0,0,0,0.15); }
    48%  { transform: translate(0, -48px); background-color: var(--tour-primary, #7cd4fd); box-shadow: none; }
    58%  { transform: translate(20px, -24px); background-color: var(--tour-primary, #7cd4fd); }
    76%  { transform: translate(20px, -24px); background-color: var(--tour-primary, #7cd4fd); }
    86%  { transform: translate(0, -24px); background-color: #bdbdbd; }
    95%  { transform: translate(0, 0);     background-color: #bdbdbd; }
    100% { transform: translate(0, 0);     background-color: #bdbdbd; }
  }
  @keyframes ${ANIMATION_ID}-cursor {
    0%   { transform: translate(0, 0);     opacity: 1; }
    6%   { transform: translate(0, -2px);  }
    16%  { transform: translate(0, 24px);  }
    26%  { transform: translate(0, 24px);  }
    28%  { opacity: 0; }
    30%  { transform: translate(0, 0);     opacity: 0; }
    32%  { transform: translate(0, 0);     opacity: 1; }
    38%  { transform: translate(0, -2px);  }
    48%  { transform: translate(0, -24px); }
    58%  { transform: translate(20px, 0);  }
    76%  { transform: translate(20px, 0);  }
    80%  { opacity: 0; }
    92%  { transform: translate(0, 0);     opacity: 0; }
    96%  { transform: translate(0, 0);     opacity: 1; }
    100% { transform: translate(0, 0);     opacity: 1; }
  }
</style>
<div style="position:relative;display:flex;flex-direction:column;gap:4px;padding:12px;background:var(--tour-border, #e0e0e0);border-radius:6px;overflow:hidden;margin-top:10px;">
  <div style="height:20px;border-radius:3px;background:#bdbdbd;"></div>
  <div style="height:20px;border-radius:3px;position:relative;z-index:1;animation:${ANIMATION_ID}-bar1 5s ease-in-out infinite;"></div>
  <div style="height:20px;border-radius:3px;position:relative;z-index:2;animation:${ANIMATION_ID}-bar2 5s ease-in-out infinite;"></div>
  <div style="position:absolute;top:40px;left:22px;z-index:3;pointer-events:none;animation:${ANIMATION_ID}-cursor 5s ease-in-out infinite;">
    <svg width="12" height="16" viewBox="0 0 12 16" fill="none">
      <path d="M1 1L1 12L3.8 8.8L6.2 14L7.8 13.2L5.4 7.6L9 7.6L1 1Z" fill="var(--tour-text, #1a1a1a)" stroke="var(--tour-bg, #fff)" stroke-width="0.8"/>
    </svg>
  </div>
</div>`;
}

export default DragDropAnimation;
